import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UserService } from './user.service';
import { ImpersonationResponse, StopImpersonationResponse, ImpersonationUser } from '../_models/user.model';
import { PermissionService } from './permission.service';
import { GlobalFilterService } from './global-filter.service';
import { formatRoleName } from '../_helpers/role-utils';

interface ImpersonatedUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  requiresDivisionFiltering: boolean;
}

interface OriginalAdmin {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
  requiresDivisionFiltering: boolean;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private readonly IMPERSONATING_KEY = 'isImpersonating';
  private readonly ORIGINAL_ADMIN_KEY = 'originalAdminData';
  private readonly IMPERSONATED_USER_KEY = 'impersonatedUserData';

  private impersonatingSubject = new BehaviorSubject<boolean>(this.checkImpersonatingState());
  
  // Observable for components to subscribe to impersonation state changes
  public isImpersonating$ = this.impersonatingSubject.asObservable();

  constructor(
    private userService: UserService,
    private permissionService: PermissionService,
    private globalFilterService: GlobalFilterService
  ) {}

  /**
   * Check if currently impersonating a user
   */
  isImpersonating(): boolean {
    return localStorage.getItem(this.IMPERSONATING_KEY) === 'true';
  }

  /**
   * Get the impersonated user data
   */
  getImpersonatedUser(): ImpersonatedUser | null {
    const data = localStorage.getItem(this.IMPERSONATED_USER_KEY);
    if (data) {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get the original admin user data
   */
  getOriginalAdmin(): OriginalAdmin | null {
    const data = localStorage.getItem(this.ORIGINAL_ADMIN_KEY);
    if (data) {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Start impersonating a user
   * @param userId The ID of the user to impersonate
   */
  startImpersonation(userId: number): Observable<ImpersonationResponse> {
    // Store the current admin's token before impersonation
    const currentToken = localStorage.getItem('token');

    return this.userService.impersonateUser(userId).pipe(
      tap(response => {
        if (response.success) {
          // Store original admin data with their token
          const originalAdminData: OriginalAdmin = {
            ...response.originalUser,
            token: currentToken || ''
          };
          localStorage.setItem(this.ORIGINAL_ADMIN_KEY, JSON.stringify(originalAdminData));
          
          // Store impersonated user data
          localStorage.setItem(this.IMPERSONATED_USER_KEY, JSON.stringify(response.impersonatedUser));
          
          // Set the impersonation flag
          localStorage.setItem(this.IMPERSONATING_KEY, 'true');
          
          // Update the current session with impersonated user's token and data
          // IMPORTANT: Include the token in the user object for the JWT interceptor
          localStorage.setItem('token', response.token);
          const impersonatedUserWithToken = {
            ...response.impersonatedUser,
            token: response.token
          };
          localStorage.setItem('currentUser', JSON.stringify(impersonatedUserWithToken));
          
          this.globalFilterService.clearGlobalFilter();
          
          // Reload permissions for the impersonated user
          this.permissionService.loadPermissions().subscribe({
            next: () => {
              console.log('Impersonated user permissions loaded');
            },
            error: (error) => {
              console.error('Error loading impersonated user permissions:', error);
            }
          });
          
          // Notify subscribers
          this.impersonatingSubject.next(true);
        }
      })
    );
  }

  /**
   * Stop impersonating and restore admin session
   */
  stopImpersonation(): Observable<StopImpersonationResponse> {
    return this.userService.stopImpersonation().pipe(
      tap(response => {
        if (response.success) {
          this.restoreAdminSession(response.token, response.user);
        }
      })
    );
  }

  /**
   * Restore admin session (used by stopImpersonation and when clearing impersonation state)
   */
  private restoreAdminSession(token: string, user: ImpersonationUser): void {
    // Clear impersonation data
    localStorage.removeItem(this.IMPERSONATING_KEY);
    localStorage.removeItem(this.ORIGINAL_ADMIN_KEY);
    localStorage.removeItem(this.IMPERSONATED_USER_KEY);
    
    // Restore admin session
    // IMPORTANT: Include the token in the user object for the JWT interceptor
    localStorage.setItem('token', token);
    const userWithToken = {
      ...user,
      token: token
    };
    localStorage.setItem('currentUser', JSON.stringify(userWithToken));
    
    this.globalFilterService.clearGlobalFilter();
    
    // Reload permissions for the admin user
    this.permissionService.loadPermissions().subscribe({
      next: () => {
        console.log('Admin permissions restored');
      },
      error: (error) => {
        console.error('Error restoring admin permissions:', error);
      }
    });
    
    // Notify subscribers
    this.impersonatingSubject.next(false);
  }

  /**
   * Clear all impersonation state (called on logout)
   */
  clearImpersonationState(): void {
    localStorage.removeItem(this.IMPERSONATING_KEY);
    localStorage.removeItem(this.ORIGINAL_ADMIN_KEY);
    localStorage.removeItem(this.IMPERSONATED_USER_KEY);
    this.impersonatingSubject.next(false);
  }

  /**
   * Get display name for the impersonated user
   */
  getImpersonatedUserDisplayName(): string {
    const user = this.getImpersonatedUser();
    if (!user) return '';
    
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || user.email;
  }

  /**
   * Get role name for the impersonated user
   */
  getImpersonatedUserRole(): string {
    const user = this.getImpersonatedUser();
    if (!user || !user.roles || user.roles.length === 0) return 'No role';
    
    // Return the first role, formatted nicely
    return formatRoleName(user.roles[0]);
  }

  /**
   * Check impersonating state from localStorage
   */
  private checkImpersonatingState(): boolean {
    return localStorage.getItem(this.IMPERSONATING_KEY) === 'true';
  }
}
